import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';

const SUB_CATEGORIES_SECTION_ID = 'section-rendering-collection-categories';

/**
 * @typedef {Object} TabbedCollectionGridRefs
 * @property {HTMLButtonElement[]} tabs
 * @property {HTMLElement} bandsList
 */

/** @extends {Component<TabbedCollectionGridRefs>} */
class TabbedCollectionGridComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#syncInitialState();
    this.#loadAllCategories();
  }

  /**
   * Filters the bands list by the clicked button's collection handle.
   * Buttons without `data-handle` (e.g. the "All" button) show every item.
   * Wired declaratively via `on:click="/select"`.
   * @param {MouseEvent} event
   */
  select(event) {
    const button = /** @type {HTMLElement | null} */ (event.target);
    if (!button) return;
    event.preventDefault();
    this.#activate(button);
  }

  async #loadAllCategories() {
    const { tabs } = this.refs;
    if (!tabs?.length) return;

    const fetchDoc = async (url) => {
      try {
        const html = await sectionRenderer.getSectionHTML(SUB_CATEGORIES_SECTION_ID, true, url);
        return new DOMParser().parseFromString(html, 'text/html');
      } catch (error) {
        console.log(error);
        return null;
      }
    };

    // Genre tabs (Music > Rock) carry data-handle. Flat categories (Gaming, TV,
    // Brands, Comics) have no genre level, so only the "All" tab exists — in that
    // case render the collection's own sub-categories directly as the cards.
    const genreTabs = tabs.filter((tab) => !!tab.dataset.handle);
    let results;
    if (genreTabs.length) {
      results = await Promise.all(
        genreTabs.map((tab) =>
          fetchDoc(new URL(`/collections/${tab.dataset.handle}`, window.location.origin))
        )
      );
    } else {
      const collectionUrl = this.dataset.collectionUrl || window.location.pathname;
      results = [await fetchDoc(new URL(collectionUrl, window.location.origin))];
    }

    // Collect every band <li> across all genres, then sort them into a single
    // flat A–Z list by license/artist name so the "All" view reads
    // alphabetically end-to-end rather than grouped by genre. Each <li> keeps
    // its data-collection handle, so per-genre filtering still works.
    const items = results.flatMap((fragment) =>
      fragment ? Array.from(fragment.querySelectorAll('ul > li')) : []
    );

    const titleOf = (li) =>
      (li.querySelector('.resource-card__title')?.textContent ?? li.textContent ?? '')
        .trim()
        .toLowerCase();

    items.sort((a, b) => titleOf(a).localeCompare(titleOf(b)));

    this.refs.bandsList.replaceChildren(...items);

    // Re-apply the current filter to freshly inserted items.
    const active = this.refs.tabs?.find((t) => t.classList.contains('is-active'));
    if (active) this.#activate(active, { focus: false });
  }

  /** Ensures one filter button is active when the component mounts. */
  #syncInitialState() {
    const { tabs } = this.refs;
    if (!tabs?.length) return;

    const active = tabs.find((t) => t.classList.contains('is-active')) ?? tabs[0];
    if (active) this.#activate(active, { focus: false });
  }

  /**
   * @param {HTMLElement} button
   * @param {{ focus?: boolean }} [options]
   */
  #activate(button, { focus = true } = {}) {
    const { tabs, bandsList } = this.refs;
    if (!tabs?.length) return;

    for (const t of tabs) {
      t.classList.toggle('is-active', t === button);
    }

    const handle = button.dataset.handle;
    for (const el of bandsList.querySelectorAll('li')) {
      const matches = !handle || el.dataset.collection === handle;
      el.classList.toggle('hidden', !matches);
    }

    if (focus) button.focus();
  }
}

customElements.define('tabbed-collection-grid-component', TabbedCollectionGridComponent);
